import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { supabase } from "../../lib/supabase";
import type { JobFilters, SvgJob } from "../../lib/types";
import { useJobStore } from "../../store/useJobStore";
import { getSvgJob, listSvgJobs, requestJobFile } from "./api";
import { isActive } from "./status";

export function useSvgJobs(filters: JobFilters) {
  return useQuery({
    queryKey: ["svg-jobs", filters.status, filters.search],
    queryFn: () => listSvgJobs(filters),
    refetchInterval: (query) => query.state.data?.some(isActive) ? 4_000 : false,
  });
}

export function useAllSvgJobs() {
  return useSvgJobs({ status: "all", search: "" });
}

export function useSvgJob(jobId: string | undefined) {
  return useQuery({
    queryKey: ["svg-job", jobId],
    enabled: Boolean(jobId),
    queryFn: () => getSvgJob(jobId!),
    refetchInterval: (query) => query.state.data && isActive(query.state.data) ? 3_000 : false,
  });
}

export function SvgJobsRealtimeBridge({ userId, children }: PropsWithChildren<{ userId: string }>) {
  const queryClient = useQueryClient();
  const setRealtimeState = useJobStore((state) => state.setRealtimeState);

  useEffect(() => {
    setRealtimeState("connecting");
    const channel = supabase
      .channel(`svg-jobs-ui:${userId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "svg_jobs",
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        void queryClient.invalidateQueries({ queryKey: ["svg-jobs"] });
        const next = payload.new as SvgJob | undefined;
        if (next?.id) queryClient.setQueryData(["svg-job", next.id], next);
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setRealtimeState("connected");
        if (["CHANNEL_ERROR", "TIMED_OUT", "CLOSED"].includes(status)) setRealtimeState("fallback");
      });

    return () => {
      void supabase.removeChannel(channel);
      setRealtimeState("connecting");
    };
  }, [queryClient, setRealtimeState, userId]);

  return children;
}

export function AutoDownloadCoordinator({ jobs }: { jobs: SvgJob[] }) {
  const autoDownload = useJobStore((state) => state.autoDownload);

  useEffect(() => {
    if (!autoDownload) return;
    for (const job of jobs) {
      if (job.status !== "completed") continue;
      const key = `svg-render:auto-downloaded:${job.id}`;
      if (window.sessionStorage.getItem(key)) continue;
      window.sessionStorage.setItem(key, "1");
      const kind = job.output_count === 1 && job.output_path ? "svg" : "zip";
      void requestJobFile(job.id, kind).then((url) => window.location.assign(url)).catch(() => {
        window.sessionStorage.removeItem(key);
      });
    }
  }, [autoDownload, jobs]);

  return null;
}
